import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Save, Trash2, X } from 'lucide-react'
import type { TicketTypeFormValues } from '../types'

const ticketTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  price: z.number().min(0, 'Price must be 0 or greater'),
  capacity: z.number().int().min(1, 'Capacity must be at least 1'),
  saleStartsAt: z.string().min(1, 'Sale start date is required'),
  saleEndsAt: z.string().min(1, 'Sale end date is required'),
  isVisible: z.boolean(),
  order: z.number().int().min(0),
})

interface Props {
  initialValues?: Partial<TicketTypeFormValues>
  onSave: (values: TicketTypeFormValues) => Promise<void>
  onDelete?: () => Promise<void>
  onCancel?: () => void
  isNew?: boolean
  defaultCapacity?: number
}

export function TicketTypeFormRow({
  initialValues,
  onSave,
  onDelete,
  onCancel,
  isNew,
  defaultCapacity,
}: Props) {
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TicketTypeFormValues>({
    resolver: zodResolver(ticketTypeSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      description: initialValues?.description ?? '',
      price: initialValues?.price ?? 0,
      capacity: initialValues?.capacity ?? defaultCapacity ?? 100,
      saleStartsAt: initialValues?.saleStartsAt ?? '',
      saleEndsAt: initialValues?.saleEndsAt ?? '',
      isVisible: initialValues?.isVisible ?? true,
      order: initialValues?.order ?? 0,
    },
  })

  async function onSubmit(values: TicketTypeFormValues) {
    setSubmitError(null)
    try {
      await onSave({ ...values, id: initialValues?.id })
    } catch (error) {
      setSubmitError(formatUserFacingError(error, 'Failed to save ticket type.'))
    }
  }

  async function handleDelete() {
    if (!onDelete) return
    setSubmitError(null)
    try {
      await onDelete()
    } catch (error) {
      setSubmitError(formatUserFacingError(error, 'Failed to delete ticket type.'))
    }
  }

  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="col-span-2 space-y-1">
          <Label className="text-xs">Name</Label>
          <Input placeholder="General Admission" {...register('name')} />
          {errors.name && <p className="text-xs text-destructive">{withErrorCode(String(errors.name.message), 'app/validation')}</p>}
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Price ($)</Label>
          <Input
            type="number"
            min={0}
            step={0.01}
            placeholder="0.00"
            {...register('price', { valueAsNumber: true })}
          />
          {errors.price && <p className="text-xs text-destructive">{withErrorCode(String(errors.price.message), 'app/validation')}</p>}
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Ticket Type Limit</Label>
          <Input
            type="number"
            min={1}
            {...register('capacity', { valueAsNumber: true })}
          />
          {errors.capacity && <p className="text-xs text-destructive">{withErrorCode(String(errors.capacity.message), 'app/validation')}</p>}
          <p className="text-xs text-muted-foreground">
            Use the show capacity unless this price tier needs a smaller allotment.
          </p>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Description (optional)</Label>
        <Input placeholder="What's included…" {...register('description')} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Sale Starts</Label>
          <Input type="datetime-local" {...register('saleStartsAt')} />
          {errors.saleStartsAt && (
            <p className="text-xs text-destructive">{withErrorCode(String(errors.saleStartsAt.message), 'app/validation')}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Sale Ends</Label>
          <Input type="datetime-local" {...register('saleEndsAt')} />
          {errors.saleEndsAt && (
            <p className="text-xs text-destructive">{withErrorCode(String(errors.saleEndsAt.message), 'app/validation')}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Sort Order</Label>
          <Input
            type="number"
            min={0}
            {...register('order', { valueAsNumber: true })}
          />
        </div>

        <div className="flex items-end gap-2 pb-0.5">
          <input
            id={`visible-${initialValues?.id ?? 'new'}`}
            type="checkbox"
            className="h-4 w-4 accent-primary"
            {...register('isVisible')}
          />
          <Label htmlFor={`visible-${initialValues?.id ?? 'new'}`} className="text-xs">
            Visible to public
          </Label>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          disabled={isSubmitting}
          onClick={() => void handleSubmit(onSubmit)()}
        >
          <Save className="h-3.5 w-3.5 mr-1.5" />
          {isSubmitting ? 'Saving…' : isNew ? 'Add' : 'Save'}
        </Button>

        {onCancel && (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-3.5 w-3.5 mr-1.5" />
            Cancel
          </Button>
        )}

        {onDelete && !isNew && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive ml-auto"
            disabled={isSubmitting}
            onClick={() => void handleDelete()}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Delete
          </Button>
        )}
      </div>
      {submitError && <p className="text-xs text-destructive">{submitError}</p>}
    </div>
  )
}
